import { goSolidApi } from "@/composables/useGoSolidApi";
import { compact, debounce, uniq } from "lodash-es";
import { onBeforeUnmount, ref, watch, type Ref } from "vue";
import { useI18n } from "vue-i18n";

const CHECK_DEBOUNCE_MS = 500;

interface CatalogCompany {
  name: string;
  account_eecs?: string | null;
}

interface CachedLookup {
  query: string;
  rows: CatalogCompany[];
}

interface EecsNameMismatchCheck {
  mismatchWarning: Ref<string>;
  unknownCounterparty: Ref<boolean>;
}

/**
 * Warns when a counterparty name and EECS account number disagree with the
 * pairs recorded in the global company catalog.
 *
 * NOTE: registries differ in which field actually routes a transfer (HKNR uses
 * the company name for domestic transfers, ignoring EECS entirely), so a
 * plausible-looking pair that belongs to two different companies must surface
 * before submit rather than being silently accepted.
 */
export function useEecsNameMismatchCheck(
  companyName: () => string,
  eecsAccountNumber: () => string
): EecsNameMismatchCheck {
  const { t } = useI18n();

  const mismatchWarning = ref("");
  const unknownCounterparty = ref(false);
  let latestRequestId = 0;
  let nameLookup: CachedLookup | null = null;
  let eecsLookup: CachedLookup | null = null;

  const clearResult = () => {
    mismatchWarning.value = "";
    unknownCounterparty.value = false;
  };

  const fetchCompanies = async (params: Record<string, string>) =>
    (
      await goSolidApi.get<CatalogCompany[]>("/api/utils/companies/", {
        params,
      })
    ).data ?? [];

  const companiesNamed = async (name: string) => {
    if (nameLookup?.query !== name) {
      nameLookup = { query: name, rows: await fetchCompanies({ name }) };
    }
    return nameLookup.rows;
  };

  const companiesWithAccount = async (eecs: string) => {
    const query = eecs.toUpperCase();
    if (eecsLookup?.query !== query) {
      eecsLookup = {
        query,
        rows: await fetchCompanies({ account_eecs: eecs }),
      };
    }
    return eecsLookup.rows;
  };

  const runCheck = async (name: string, eecs: string) => {
    const requestId = latestRequestId;

    try {
      const [companiesByName, companiesByEecs] = await Promise.all([
        companiesNamed(name),
        companiesWithAccount(eecs),
      ]);

      if (requestId !== latestRequestId) return;

      const pairConfirmed = companiesByName.some(
        (company) =>
          company.account_eecs?.trim().toUpperCase() === eecs.toUpperCase()
      );
      if (pairConfirmed) {
        clearResult();
        return;
      }

      const expectedEecs = uniq(
        compact(companiesByName.map((company) => company.account_eecs))
      ).join(", ");
      const expectedName = uniq(
        companiesByEecs.map((company) => company.name)
      ).join(", ");

      unknownCounterparty.value = !companiesByName.length && !expectedName;

      if (expectedEecs && expectedName) {
        mismatchWarning.value = t("common.eecs_name_mismatch.both", {
          name,
          eecs,
          expectedEecs,
          expectedName,
        });
      } else if (expectedName) {
        mismatchWarning.value = t("common.eecs_name_mismatch.eecs_only", {
          name,
          eecs,
          expectedName,
        });
      } else if (expectedEecs) {
        mismatchWarning.value = t("common.eecs_name_mismatch.name_only", {
          name,
          eecs,
          expectedEecs,
        });
      } else {
        mismatchWarning.value = "";
      }
    } catch {
      if (requestId !== latestRequestId) return;
      // NOTE: deliberately silent. An advisory lookup that fails must not
      // surface an error dialog over a form the user can still submit.
      clearResult();
    }
  };

  const debouncedRunCheck = debounce(runCheck, CHECK_DEBOUNCE_MS);

  watch(
    [companyName, eecsAccountNumber],
    ([name, eecs]) => {
      latestRequestId++;
      clearResult();

      const trimmedName = name.trim();
      const trimmedEecs = eecs.trim();
      if (!trimmedName || !trimmedEecs) {
        debouncedRunCheck.cancel();
        return;
      }

      debouncedRunCheck(trimmedName, trimmedEecs);
    },
    { immediate: true }
  );

  onBeforeUnmount(() => {
    latestRequestId++;
    debouncedRunCheck.cancel();
  });

  return { mismatchWarning, unknownCounterparty };
}
